import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FleetChargingFigureService } from 'src/app/_services/fleet-charging-figure.service';
import { BatteryTypeOption, toBatteryTypeFilter, toBatteryTypeSeries } from 'src/app/_shared/utils/battery-type';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';

@Component({
  selector: 'app-equivalent-full-charge-cycles-chart',
  templateUrl: './equivalent-full-charge-cycles-chart.component.html',
  styleUrls: ['./equivalent-full-charge-cycles-chart.component.scss']
})
export class EquivalentFullChargeCyclesChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() selectedBatteryTypes: BatteryTypeOption[] = [];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  chartRange!: { range: [number, number] };
  chartYAxisRange!: { range: [number, number] };
  
  constructor(
    private readonly fleetChargingFigureService: FleetChargingFigureService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  
  ngOnChanges(): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetChargingFigureService.getFullChargeCyclesFleet(this.fleetId, toBatteryTypeFilter(this.selectedBatteryTypes)).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          this.plotEquivalentFullChargeCycelesChart(datapoints);
        },
        error: (error) => {
          console.error('Error fetching Equivalent Full Cycles Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.dataLoading = false;
        }
      }
    )
  }
  
  plotEquivalentFullChargeCycelesChart(datapoints: any) {
    const transformXAxisPipe = new TransformXAxisPipe();
    const data = toBatteryTypeSeries(datapoints, this.selectedBatteryTypes).map((series) => ({
      x: series.datapoints.map((dataObject: any) => dataObject.binCycle),
      y: series.datapoints.map((dataObject: any) => dataObject.percVeh),
      type: 'bar',
      name: series.label,
      marker: {
        color: series.color
      }
    }));
    const cycles = data.flatMap((trace) => trace.x);
    const values = data.flatMap((trace) => trace.y);
    this.chartRange = transformXAxisPipe.transform(cycles, values, 30, 10000);
    this.chartYAxisRange = transformXAxisPipe.filterYValues(cycles, values, 0, 10000);
    
    const layout = {
      title: 'Equivalent Full Cycles',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'Equivalent full cycles',
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Fleet Vehicles [%]',
        range: this.chartYAxisRange?.range
      },
      showlegend: true,
      legend: {
        orientation: 'h', 
        y: -0.3, 
        xanchor: 'center', 
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };
    
    createPlotlyChart('equivalent-full-charge-cycles', data, layout);
  }
}